/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, IDetailsRowProps } from '@fluentui/react/lib/DetailsList';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, IRenderFunction } from '@fluentui/react/lib/Utilities';
import { Observer, observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisMigratedAppStore } from 'src/clu/core/stores/LuisMigratedAppStore';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';

export type LuisApplicationsTableProps = TableProps<LuisMigratedAppStore> & {
    pageCount: number;
    loadAppVersions: (appId: string) => void;
    onRenderRow: IRenderFunction<IDetailsRowProps>;
    onVersionChange: (item: LuisMigratedAppStore, option: IDropdownOption) => void;
    setVersionDropdownOptions: (appId: string) => IDropdownOption<LuisMigratedAppStore>[];
};

const messages = defineMessages({
    name: {
        id: 'cluMigration.luisApplicationTable.name',
        defaultMessage: 'Name'
    },
    culture: {
        id: 'cluMigration.luisApplicationTable.culture',
        defaultMessage: 'Culture'
    },
    version: {
        id: 'cluMigration.luisApplicationTable.version',
        defaultMessage: 'Version'
    }
});

const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});

export const LuisApplicationsTable = observer((props: LuisApplicationsTableProps) => {
    const {
        items,
        sortKey,
        pageCount,
        selection,
        onRenderRow,
        loadAppVersions,
        onVersionChange,
        setVersionDropdownOptions,
        ...rest
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();
    const { cluMigrationStoreHouse } = rootStore;
    const { luisMigratedAppsStoreHouse } = cluMigrationStoreHouse;

    const getTableHeight = isLargeOrGreater ? (pageCount > 1 ? '270px' : '350px') : pageCount > 1 ? '250px' : '300px';

    const columns: IColumn[] = [
        {
            key: 'name',
            name: localize(messages.name),
            fieldName: 'name',
            minWidth: 100,
            maxWidth: 150,
            isRowHeader: true,
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: LuisMigratedAppStore) => (
                <TruncatedText block nowrap maxWidth={200}>
                    {item.name}
                </TruncatedText>
            )
        },
        {
            key: 'culture',
            name: localize(messages.culture),
            fieldName: 'culture',
            minWidth: 100,
            maxWidth: 125,
            isResizable: true,
            isSorted: sortKey?.key === 'culture',
            isSortedDescending: sortKey?.key === 'culture' && sortKey?.order === 'desc',
            onRender: (item: LuisMigratedAppStore) => <LuisText>{item.culture}</LuisText>
        },
        {
            key: 'selectedVersion',
            name: localize(messages.version),
            fieldName: 'version',
            minWidth: 70,
            maxWidth: 100,
            isResizable: true,
            isSorted: sortKey?.key === 'selectedVersion',
            isSortedDescending: sortKey?.key === 'selectedVersion' && sortKey?.order === 'desc',
            onRender: (item: LuisMigratedAppStore) => (
                <Observer>
                    {() => (
                        <LuisDropdown
                            isSearchEnabled={false}
                            data-selection-disabled
                            title={item.selectedVersion}
                            selectedKey={item.selectedVersion}
                            options={setVersionDropdownOptions(item.id)}
                            onChange={(_, option) => onVersionChange(item, option)}
                            styles={{ root: { minWidth: '70px', maxWidth: '70px' } }}
                            areOptionsLoading={luisMigratedAppsStoreHouse.findById(item.id).areVersionsLoading}
                            onRenderTitle={() => (
                                <TruncatedText block nowrap maxWidth={70}>
                                    {item.selectedVersion}
                                </TruncatedText>
                            )}
                            calloutProps={{
                                onLayerMounted: () => !item.luisApp.appVersionStoreHouse.items.length && loadAppVersions(item.id)
                            }}
                        />
                    )}
                </Observer>
            )
        }
    ];

    return (
        <ScrollablePane styles={mergeStyleSets(ScrollablePaneStyles, { root: { minHeight: getTableHeight } })}>
            <Table<LuisMigratedAppStore>
                items={items}
                columns={columns}
                selection={selection}
                onRenderRow={onRenderRow}
                selectionPreservedOnEmptyClick
                {...rest}
            />
        </ScrollablePane>
    );
});
